import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { SecurityVerificationEnum, SecurityVerificationType, SelectOption, Signer } from '@app/models';
import { DocumentService } from '@app/services';
import { AlertModalService } from '@app/shared';
import { TranslateService } from '@ngx-translate/core';

export enum ManualBiometryApprovalEnum {
  Accepted = 'accept',
  Rejected = 'rejected',
  ResetBiometryAttempt = 'reset_biometry_attempt'
}
export type ModalResult = { choice: ManualBiometryApprovalEnum; verification: SecurityVerificationType };
export interface ModalPublicProperties {
  signer: Signer;
  hideManualApproval?: boolean;
}

@Component({
  selector: 'app-verification-data-modal',
  templateUrl: './verification-data-modal.component.html',
  styleUrls: ['./verification-data-modal.component.scss'],
  standalone: false
})
export class VerificationDataModalComponent implements ModalPublicProperties, OnInit {
  @Input() signer: Signer;
  @Input() hideManualApproval: boolean;
  signerVerification: SecurityVerificationType;
  isLoading = false;
  canApproveManually = false;
  faceImages: string[] = [];
  docsImages: string[] = [];
  resetBiometryAttempt = false;
  attemptOptions: SelectOption[] = [];
  attemptMap = new Map<string, any>();
  selectedAttemptKey: string;
  readonly SecurityVerificationEnum = SecurityVerificationEnum;
  readonly ManualBiometryApprovalEnum = ManualBiometryApprovalEnum;

  constructor(
    public modal: NgbActiveModal,
    private alertModalService: AlertModalService,
    private documentService: DocumentService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.signerVerification = this.documentService.getPendingManualVerification(this.signer);

    if (this.signerVerification) {
      this.canApproveManually =
        !this.hideManualApproval &&
        !this.signerVerification.verified_at &&
        this.documentService.isSignerUnapproved(this.signer) &&
        this.documentService.allowedManualVerifications.includes(this.signerVerification.type);

      if (Array.isArray(this.signerVerification.user.images)) {
        if (this.signerVerification.type === SecurityVerificationEnum.Upload) {
          this.docsImages = this.signerVerification.user.images;
        } else if (this.signerVerification.type === SecurityVerificationEnum.PfFacial) {
          this.faceImages = this.signerVerification.user.images;
        } else {
          this.faceImages = this.signerVerification.user.images.slice(-1, this.signerVerification.user.images.length); // Última
          this.docsImages = this.signerVerification.user.images.slice(0, -1); // Todas menos a última
        }
      } else {
        const images = this.signerVerification.user.images || {};
        this.docsImages = this.signerVerification.type === SecurityVerificationEnum.PfFacial ? [] : [images.front, images.back].filter(Boolean);
        this.faceImages = this.signerVerification.type === SecurityVerificationEnum.Upload ? [] : [images.selfie].filter(Boolean);
      }

      if (this.canApproveManually && this.signerVerification?.logs_attempt?.length > 0) {
        this.attemptOptions = this.getAttemptOptions();

        const lastAttempt = this.attemptOptions[this.attemptOptions.length - 1];
        if (lastAttempt) {
          this.selectedAttemptKey = lastAttempt.key;
          this.updateImagesFromKey(this.selectedAttemptKey);
        }
      }
    }
  }

  submit(choice: ManualBiometryApprovalEnum) {
    let confirmationModal$;
    switch (choice) {
      case ManualBiometryApprovalEnum.Accepted:
        confirmationModal$ = this.alertModalService.confirmation({
          text: this.translateService.instant('alerts.acceptSignature'),
          confirmButtonText: this.translateService.instant('button.accept'),
          closeButtonText: this.translateService.instant('button.cancel')
        });
        break;

      case ManualBiometryApprovalEnum.Rejected:
        confirmationModal$ = this.alertModalService.warning({
          text: this.translateService.instant('alerts.rejectSignature'),
          confirmButtonText: this.translateService.instant('button.reject'),
          closeButtonText: this.translateService.instant('button.cancel')
        });
        break;

      case ManualBiometryApprovalEnum.ResetBiometryAttempt:
        confirmationModal$ = this.alertModalService.warning({
          text: this.translateService.instant('alerts.resetBiometryAttempt'),
          confirmButtonText: this.translateService.instant('button.request'),
          closeButtonText: this.translateService.instant('button.cancel')
        });
        break;
    }

    if (confirmationModal$) {
      confirmationModal$.subscribe({
        next: () => this.modal.close({ choice, verification: this.signerVerification } as ModalResult)
      });
    }
  }

  getVerificationAt() {
    return this.signerVerification?.verified_at || this.signer?.biometric_auto_rejected?.created_at || this.signer?.signed_unapproved?.created_at;
  }

  getErrorMessage() {
    const logs = this.signerVerification?.logs_attempt || [];
    const lastLog = logs[logs.length - 1];

    if (lastLog?.message) {
      return lastLog?.message;
    } else if (this.signerVerification?.payload?.code && this.signerVerification?.payload?.code === 'cpf_mismatch') {
      return this.translateService.instant('biometricErrors.' + String(this.signerVerification.payload.code).toLowerCase());
    }
  }

  hasCpfMismatch() {
    return this.signerVerification?.payload?.code === 'cpf_mismatch' && this.signerVerification?.user?.extracted_data?.cpf && this.signer?.configs?.cpf && this.signer?.user_data?.cpf;
  }

  getAttemptOptions(): SelectOption[] {
    const logs = this.signerVerification?.logs_attempt ?? [];
    const attempts = logs.filter((log: any) => log.attempt && log.type !== 'manual');
    return attempts.map((log: any, index: number) => {
      const key = String(index);
      this.attemptMap.set(key, log);
      return {
        key,
        value: this.translateService.instant('documents.attempt', { attempt: index + 1 })
      };
    });
  }

  updateImagesFromKey(key: string) {
    const attempt = this.attemptMap.get(key);

    if (attempt?.image) {
      this.faceImages = [attempt?.image];
    }

    if (attempt?.images) {
      const { front, back, selfie } = attempt.images;
      this.docsImages = [front, back].filter(Boolean);
      this.faceImages = [selfie].filter(Boolean);
    }
  }
}
